import { useMutation } from 'react-query';
import { PaymentOrigin } from '@frontend/api-invoices';
import { PaymentMethod, PaymentPlanDetailsResponse } from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import { useConfirmStripePaymentViaPaymentMethod } from '@frontend/payments-collection-flow';
import { useLatencyBustPoll } from '@frontend/payments-hooks';
import { useAlert } from '@frontend/design-system';
import { usePaymentPlanDetails } from '../payment-plans/use-payment-plan-details';

interface UseOneTimePaymentOptions {
  email?: string;
  amount: number;
  planDetails: PaymentPlanDetailsResponse;
  paymentMethod?: PaymentMethod;
}

export const useOneTimePayment = ({ amount, email, planDetails, paymentMethod }: UseOneTimePaymentOptions) => {
  const { t } = useTranslation('payments');
  const alerts = useAlert();
  const locationId = planDetails?.locationId;
  const personId = planDetails?.person.id;
  const pmId = planDetails?.person.pmId;

  const { makePayment } = useConfirmStripePaymentViaPaymentMethod({
    amount,
    email,
    pmId,
    personId,
    locationId,
    origin: PaymentOrigin.ManualCardEntry,
    paymentMethod,
    planDetails,
  });

  const { refetch, refetchDetails } = usePaymentPlanDetails({ paymentPlanId: planDetails.id });

  const { getPollPromise } = useLatencyBustPoll({
    queryMethod: async () => {
      const { data } = await refetchDetails();
      return data;
    },
    onPollEnd: () => {
      refetch();
    },
    accessor: (planDetails) => {
      return planDetails?.paymentSummary.remainingAmount;
    },
  });

  const { mutateAsync: makeOneTimePayment, isLoading: makingOneTimePayment } = useMutation({
    mutationFn: () => makePayment(),
    onSuccess: async (paymentId) => {
      alerts.info(t('The payment is being processed. Please do not make another payment until it shows in the list.'));
      if (paymentId) {
        await getPollPromise(planDetails);
      }
    },
  });

  return { makeOneTimePayment, makingOneTimePayment };
};
